import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import SelectField from '../common/SelectField.js';
import { DEFAULT_ARTICLE_TYPE_OPTIONS } from '../../utils/cargoParcelUtils.js';

/**
 * Shared article / package type picker.
 * Preset options + real-time custom Add that writes straight into the field.
 */
const ArticleTypeSelect = ({
  value = '',
  onChange,
  readOnly = false,
  className = '',
  placeholder = 'Select type',
}) => {
  const [extraOptions, setExtraOptions] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');

  const current = (value || '').toString().trim().toUpperCase();

  const options = useMemo(() => {
    const merged = [
      ...DEFAULT_ARTICLE_TYPE_OPTIONS,
      ...extraOptions,
      ...(current ? [current] : []),
    ];
    return [...new Set(merged.filter(Boolean))];
  }, [extraOptions, current]);

  const commitCustom = () => {
    const next = draft.trim().toUpperCase();
    if (!next) return;
    if (!DEFAULT_ARTICLE_TYPE_OPTIONS.includes(next)) {
      setExtraOptions((prev) => (prev.includes(next) ? prev : [...prev, next]));
    }
    onChange(next);
    setDraft('');
    setAdding(false);
  };

  const cancelAdd = () => {
    setDraft('');
    setAdding(false);
  };

  if (readOnly) {
    return (
      <div className={className}>
        {current
          ? current.charAt(0) + current.slice(1).toLowerCase()
          : '—'}
      </div>
    );
  }

  if (adding) {
    return (
      <div className="flex gap-1.5 min-w-0 items-center">
        <input
          type="text"
          autoFocus
          value={draft}
          placeholder="Type name"
          onChange={(e) => setDraft(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitCustom();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              cancelAdd();
            }
          }}
          className={`${className} flex-1 uppercase`}
        />
        <button
          type="button"
          onClick={commitCustom}
          className="shrink-0 px-2 py-1.5 text-xs font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg"
          title="Add"
        >
          Add
        </button>
        <button
          type="button"
          onClick={cancelAdd}
          className="shrink-0 p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg"
          title="Cancel"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <SelectField
      variant="form"
      portal
      value={current || ''}
      onChange={(e) => {
        const v = e.target.value;
        if (v === '__ADD_NEW__') {
          setAdding(true);
          setDraft('');
          return;
        }
        onChange(v);
      }}
      className="w-full min-w-0"
      triggerClassName={`${className} bg-white text-gray-700`}
    >
      <option value="__ADD_NEW__" className="text-blue-600 font-semibold border-b border-gray-100">
        + Add new…
      </option>
      {!current && <option value="">{placeholder}</option>}
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt.charAt(0) + opt.slice(1).toLowerCase()}
        </option>
      ))}
    </SelectField>
  );
};

export default ArticleTypeSelect;
